import i18n from 'i18next'
import type { ElplanErrorEnvelope, ServerErrorResponse } from './types'

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

export type ElplanErrorIdentity = Partial<ElplanErrorEnvelope> & {
  /** Set only for a legacy body that predates the `{ code }` envelope. */
  legacyText?: string
  status?: number
}

function getErrorBody(error: unknown): unknown {
  if (!isRecord(error)) {
    return undefined
  }
  return error.body
}

export function getElplanErrorIdentity(error: unknown): ElplanErrorIdentity {
  const identity = getBodyIdentity(error)
  return isRecord(error) && typeof error.status === 'number' ? { ...identity, status: error.status } : identity
}

// Backend errors arrive either as the `{ code, message, details }` envelope or as a legacy body
// (a string, or `{ detail }`/`{ message }`); this reduces both to one shape.
function getBodyIdentity(error: unknown): ElplanErrorIdentity {
  const body = getErrorBody(error)
  if (typeof body === 'string') {
    return body.trim() === '' ? {} : { legacyText: body }
  }
  if (isRecord(body)) {
    const code = typeof body.code === 'string' ? body.code : undefined
    const message = typeof body.message === 'string' ? body.message : undefined
    const details = isRecord(body.details) ? body.details : undefined
    if (code) {
      return { code, message, details }
    }
    const legacyText =
      typeof body.detail === 'string' ? body.detail : typeof body.message === 'string' ? body.message : undefined
    return legacyText ? { legacyText } : {}
  }
  return {}
}

/** Returns the first non-empty string from a legacy `{ error: [...] }` validation body. */
export function getElplanValidationError(error: unknown): string | undefined {
  const body = getErrorBody(error)
  if (!isRecord(body) || !Array.isArray(body.error)) {
    return undefined
  }

  for (const message of body.error) {
    if (typeof message === 'string') {
      const trimmedMessage = message.trim()
      if (trimmedMessage !== '') {
        return trimmedMessage
      }
    }
  }

  return undefined
}

/** Returns the first DRF `non_field_errors` message when present and non-empty. */
export function getFirstNonFieldError(error: unknown): string | undefined {
  if (!isRecord(error)) {
    return undefined
  }

  const maybeMessages = Array.isArray(error.non_field_errors)
    ? error.non_field_errors
    : isRecord(error.body) && Array.isArray(error.body.non_field_errors)
      ? error.body.non_field_errors
      : undefined

  if (!maybeMessages) {
    return undefined
  }

  for (const message of maybeMessages) {
    if (typeof message === 'string') {
      const trimmedMessage = message.trim()
      if (trimmedMessage !== '') {
        return trimmedMessage
      }
    }
  }

  return undefined
}

export function getErrorBodyText(error: ServerErrorResponse): string {
  const { body } = error
  if (typeof body === 'string') {
    return body
  }
  if (body && typeof body === 'object') {
    if ('detail' in body && typeof body.detail === 'string') {
      return body.detail
    }
    if ('message' in body && typeof body.message === 'string') {
      return body.message
    }
  }
  return i18n.t('Something went wrong, try again or contact us.')
}
